import type { CSSProperties, ReactNode } from 'react';
import { Box, useMantineTheme } from '@mantine/core';
import { DEFAULT_QR_CODE_COLOR } from '@/theme';
import classes from './PageHeader.module.css';

export type PageHeaderVariant = 'hero' | 'section';

export type PageHeaderSpacing = number | string;

function resolveSpacing(
  value: PageHeaderSpacing | undefined,
  spacing: Record<string, string>
): string | number | undefined {
  if (value === undefined) return undefined;
  if (typeof value === 'number') return value;
  return spacing[value] ?? value;
}

export type PageHeaderProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  eyebrow?: string;
  icon?: ReactNode;
  leading?: ReactNode;
  accentColor?: string;
  variant?: PageHeaderVariant;
  titleAs?: 'h1' | 'h2' | 'h3';
  mb?: PageHeaderSpacing;
  mt?: PageHeaderSpacing;
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
  align?: 'left' | 'center';
};

export function PageHeader({
  title,
  subtitle,
  eyebrow,
  icon,
  leading,
  accentColor = DEFAULT_QR_CODE_COLOR,
  variant = 'hero',
  titleAs,
  mb,
  mt,
  actions,
  children,
  className,
  align = 'left',
}: PageHeaderProps) {
  const theme = useMantineTheme();
  const Heading = titleAs ?? (variant === 'hero' ? 'h1' : 'h2');
  const showSectionMarker = variant === 'section' && !icon && !leading;
  const indentBody = Boolean(children && (icon || leading || showSectionMarker));
  const bodyClassName = [
    classes.body,
    indentBody ? classes.bodyIndented : '',
    indentBody && showSectionMarker ? classes.bodyMarkerIndented : '',
  ]
    .filter(Boolean)
    .join(' ');

  const rootStyle = {
    '--page-header-accent': accentColor,
    marginBottom: resolveSpacing(mb, theme.spacing),
    marginTop: resolveSpacing(mt, theme.spacing),
  } as CSSProperties;

  return (
    <header
      className={`${classes.root} ${classes[variant]} ${align === 'center' ? classes.centered : ''} ${className ?? ''}`.trim()}
      style={rootStyle}
    >
      <div className={classes.topRow}>
        <div className={classes.main}>
          {leading ? (
            <div className={classes.leading}>{leading}</div>
          ) : icon ? (
            <div className={classes.iconBox} aria-hidden>
              {icon}
            </div>
          ) : showSectionMarker ? (
            <div className={classes.sectionMarker} aria-hidden />
          ) : null}

          <div className={classes.textBlock}>
            {eyebrow ? <span className={classes.eyebrow}>{eyebrow}</span> : null}
            <Heading className={classes.title}>{title}</Heading>
            {subtitle ? <div className={classes.subtitle}>{subtitle}</div> : null}
            <div className={classes.accentLine} aria-hidden />
          </div>
        </div>

        {actions ? <div className={classes.actions}>{actions}</div> : null}
      </div>

      {children ? <Box className={bodyClassName}>{children}</Box> : null}
    </header>
  );
}
